import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Banner, Button, Checkbox, Dialog, List, ListItem, Select, Skeleton, TextField } from '@meshble/molo';
import { api, must, type components } from '../lib/api';
import { ramiDaLeggere, trovaRepo, unioneModuli } from '../lib/catalogo';
import { NomeModulo } from './catalogo';
import { Pacchetto } from '../icons';
import { t } from '../lib/lingua';

type RepoCatalogo = components['schemas']['RepoCatalogo'];

/**
 * Il ramo e i moduli di un repository del catalogo dentro un blueprint,
 * scelti fra quelli veri invece che scritti a mano.
 *
 * Senza ramo fissato il blueprint prende la major di ogni istanza, quindi i
 * moduli si leggono da tutte le major che Molo gestisce e il repository ha:
 * accanto a ognuno, le versioni su cui non arriverebbe. Le chiavi delle
 * domande sono quelle della pagina del catalogo: le risposte si condividono.
 */
export function ModuliDelCatalogo({
  slug, repo, ramo, onRamo, scelti, onScelti,
}: {
  slug: string;
  repo: RepoCatalogo;
  ramo: string;
  onRamo: (ramo: string) => void;
  scelti: string[];
  onScelti: (moduli: string[]) => void;
}) {
  const { owner, name } = repo;
  const [filtro, setFiltro] = useState('');
  const rami = useQuery({
    queryKey: ['catalogo-rami', slug, owner, name],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/catalog/{owner}/{name}/branches', { params: { path: { slug, owner, name } } })),
  });
  const versioni = useQuery({
    queryKey: ['odoo-versions'],
    queryFn: () => must(api.GET('/api/v1/odoo/versions')),
    staleTime: Infinity,
  });
  const tutti = rami.data?.branches ?? [];
  const daLeggere = ramiDaLeggere(ramo, tutti, (versioni.data ?? []).map((v) => v.major));
  const letti = useQueries({
    queries: daLeggere.map((r) => ({
      queryKey: ['catalogo-moduli', slug, owner, name, r],
      queryFn: () =>
        must(api.GET('/api/v1/orgs/{slug}/catalog/{owner}/{name}/modules', {
          params: { path: { slug, owner, name }, query: { branch: r } },
        })),
      staleTime: 5 * 60_000,
    })),
  });
  const perRamo: Record<string, string[]> = {};
  letti.forEach((q, i) => {
    const r = daLeggere[i];
    if (q.data && r) perRamo[r] = q.data.modules ?? [];
  });
  const disponibili = unioneModuli(perRamo);
  const inCarico = rami.isPending || versioni.isPending || letti.some((q) => q.isPending);
  const errore = rami.error ?? letti.find((q) => q.error)?.error;
  // Scelti prima — a mano, o su un ramo che poi è cambiato — e che nei rami
  // letti non ci sono: restano in vista, spuntati, finché non li si toglie.
  const fuori = scelti
    .filter((m) => !disponibili.some((d) => d.nome === m))
    .map((nome) => ({ nome, mancaIn: daLeggere }));
  const cerca = filtro.trim().toLowerCase();
  // I moduli scelti in cima: in un repository come OCA/web sono tre su centocinquanta.
  const visibili = [...fuori, ...disponibili.filter((m) => m.nome.includes(cerca))]
    .sort((a, b) => Number(scelti.includes(b.nome)) - Number(scelti.includes(a.nome)));
  const spunta = (nome: string, si: boolean) =>
    onScelti(si ? [...scelti, nome] : scelti.filter((m) => m !== nome));

  return (
    <>
      <Select
        label={t('Ramo')}
        value={ramo}
        onChange={(e) => onRamo(e.target.value)}
        options={[
          { value: '', label: t('La versione dell’istanza') },
          ...(ramo && !tutti.includes(ramo) ? [ramo] : []).concat(tutti).map((r) => ({ value: r, label: r })),
        ]}
        help={ramo
          ? t('Fisso: ogni istanza prende questo ramo, di qualunque versione sia.')
          : t('Ogni istanza prende il ramo della sua versione di Odoo: lo stesso blueprint va su una 17 e su una 18.')}
      />
      {errore && <Banner tone="error" title={t('Non riesco a leggere i moduli')}>{errore.message}</Banner>}
      {inCarico ? (
        <Skeleton height="6rem" />
      ) : (
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-2)' }}>
          <span className="md-body-small" style={{ color: scelti.length === 0 ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-on-surface-variant)' }}>
            {scelti.length === 0 ? t('Scegli almeno un modulo.') : scelti.length === 1 ? t('1 da installare') : t('{n} da installare', { n: scelti.length })}
          </span>
          {daLeggere.length === 0 && (
            <p className="md-body-medium" style={{ margin: 0 }}>
              {t('Il repository non ha rami col nome di una versione di Odoo: scegline uno.')}
            </p>
          )}
          {disponibili.length > 8 && (
            <TextField
              label=""
              aria-label={t('Cerca un modulo')}
              placeholder={t('Cerca un modulo…')}
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          )}
          <div className="md-griglia-moduli">
            {visibili.map((m) => (
              <Checkbox
                key={m.nome}
                checked={scelti.includes(m.nome)}
                onCheckedChange={(v) => spunta(m.nome, v === true)}
                label={
                  <span>
                    <span className="md-mono"><NomeModulo nome={m.nome} /></span>
                    {m.mancaIn.length > 0 && (
                      <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                        {` · ${dove(m.mancaIn, daLeggere)}`}
                      </span>
                    )}
                  </span>
                }
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** Dove manca un modulo, detto nel modo più corto: «solo nel 16.0» invece di quattro versioni. */
function dove(mancaIn: string[], rami: string[]): string {
  const presente = rami.filter((r) => !mancaIn.includes(r));
  return presente.length > 0 && presente.length < mancaIn.length
    ? t('solo nel {rami}', { rami: presente.join(', ') })
    : t('non nel {rami}', { rami: mancaIn.join(', ') });
}

const MOSTRATI = 30;

/** «Aggiungi dal catalogo»: si cerca un repository e lo si mette nel blueprint. */
export function ScegliDalCatalogo({
  open, onOpenChange, repos, errore, onScelto,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  repos: RepoCatalogo[];
  errore?: Error | null;
  onScelto: (repo: RepoCatalogo) => void;
}) {
  const [cerca, setCerca] = useState('');
  const trovati = trovaRepo(repos, cerca, '');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Aggiungi dal catalogo')}
      description={t('I repository di OCA e delle vostre organizzazioni GitHub: dopo scegli il ramo e i moduli.')}
      actions={<Button variant="text" onClick={() => onOpenChange(false)}>{t('Annulla')}</Button>}
    >
      <div className="md-stack">
        {errore && <Banner tone="error" title={t('Non riesco a leggere il catalogo')}>{errore.message}</Banner>}
        <TextField
          label=""
          aria-label={t('Cerca un repository')}
          placeholder={t('Cerca per nome o descrizione: web, account, l10n-italy…')}
          value={cerca}
          autoFocus
          onChange={(e) => setCerca(e.target.value)}
        />
        {trovati.length === 0 ? (
          <p className="md-body-medium" style={{ margin: 0 }}>{t('Nessun repository corrisponde.')}</p>
        ) : (
          <List>
            {trovati.slice(0, MOSTRATI).map((r) => (
              <ListItem
                key={r.url}
                interactive
                icon={<Pacchetto />}
                headline={`${r.owner}/${r.name}`}
                supporting={r.description}
                onClick={() => {
                  onScelto(r);
                  setCerca('');
                }}
              />
            ))}
          </List>
        )}
        {trovati.length > MOSTRATI && (
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Scrivi per restringere: ne mostro {n} su {tot}.', { n: MOSTRATI, tot: trovati.length })}
          </p>
        )}
      </div>
    </Dialog>
  );
}
